import { Fragment, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { MediaCard } from "../components/MediaCard";
import { Lightbox } from "../components/Lightbox";
import { Reveal, MaskLines } from "../components/Reveal";
import { EmptyState } from "../components/EmptyState";
import { AdSlot } from "../components/Ads";
import { http, asList } from "../lib/api";

const TITLES = {
  video: ["Todos os", "vídeos."],
  photo: ["Ensaios", "fotográficos."],
  trending: ["Em alta", "agora."],
  liked: ["Mais", "curtidos."],
};

export default function Browse({ mode }) {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const [sort, setSort] = useState(mode === "trending" ? "popular" : mode === "liked" ? "liked" : "latest");
  const [term, setTerm] = useState(q);
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lb, setLb] = useState(-1);
  const type = mode === "photo" ? "photo" : mode === "video" || mode === "liked" ? "video" : undefined;

  useEffect(() => {
    setTerm(q);
  }, [q]);

  useEffect(() => {
    http.get("/categories").then((r) => setCats(Array.isArray(r.data?.categories) ? r.data.categories : [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    http.get("/media", { params: { type, q: q || undefined, category: category || undefined, sort, limit: 120, pinned_first: mode === "video" || undefined } })
      .then((r) => setItems(asList(r)))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [type, q, category, sort]);

  const update = (k, v) => {
    const next = new URLSearchParams(params);
    v ? next.set(k, v) : next.delete(k);
    setParams(next);
  };

  const [l1, l2] = TITLES[mode];
  const photos = items.filter((i) => i.type === "photo");

  return (
    <div className="mx-auto min-h-screen max-w-[1440px] px-5 pb-10 pt-36 sm:px-10" data-testid={`browse-page-${mode}`}>
      <h1 className="font-serif-d mb-12 text-5xl font-semibold leading-[0.95] tracking-tight text-white sm:text-7xl">
        <MaskLines lines={[l1, <span key="i" className="italic text-rose-500">{l2}</span>]} />
      </h1>

      <div className="mb-12 flex flex-col gap-6 border-y border-white/5 py-6 lg:flex-row lg:items-center lg:justify-between">
        <form onSubmit={(e) => { e.preventDefault(); update("q", term.trim()); }} className="flex items-center gap-3 border-b border-slate-700 pb-2 focus-within:border-rose-500 lg:w-80">
          <Search size={16} className="text-slate-400" />
          <input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Buscar por título ou tag" data-testid="content-search-input" className="flex-1 bg-transparent text-white outline-none placeholder:text-slate-500" />
        </form>
        <div className="flex flex-wrap gap-2" data-testid="category-filter">
          {["", ...cats].map((c) => (
            <button key={c || "all"} onClick={() => update("category", c)} data-testid={`filter-category-${c || "todas"}`} className={`px-4 py-2 text-xs uppercase tracking-[0.18em] transition-colors ${category === c ? "bg-rose-600 text-white" : "border border-slate-700 text-slate-300 hover:border-rose-500 hover:text-white"}`}>
              {c || "Todas"}
            </button>
          ))}
        </div>
        <div className="flex gap-1 font-mono-d text-xs" data-testid="sort-toggle">
          {[["latest", "Recentes"], ["popular", "Mais vistos"], ["liked", "Mais curtidos"]].map(([k, l]) => (
            <button key={k} onClick={() => setSort(k)} data-testid={`sort-${k}`} className={`px-3 py-2 uppercase tracking-widest transition-colors ${sort === k ? "text-rose-400" : "text-slate-400 hover:text-white"}`}>{l}</button>
          ))}
        </div>
      </div>

      {q && <p className="mb-8 text-sm text-slate-300" data-testid="search-summary">{items.length} resultado(s) para “<span className="text-white">{q}</span>”</p>}

      {loading ? (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map((i) => <div key={i} className="aspect-video animate-pulse bg-white/5" />)}</div>
      ) : items.length === 0 ? (
        <EmptyState testid="browse-empty" text={q || category ? "Nada encontrado para esse filtro." : "Ainda não há conteúdo publicado aqui."} />
      ) : (
        <div className={`grid gap-x-6 gap-y-12 ${mode === "photo" ? "grid-cols-2 md:grid-cols-3 lg:grid-cols-4" : "sm:grid-cols-2 lg:grid-cols-3"}`} data-testid={mode === "photo" ? "photo-gallery" : "video-gallery"}>
          {items.map((it, i) => (
            <Fragment key={it.id}>
              {i === 6 && <AdSlot placement="gallery" className="col-span-full py-4" />}
              <Reveal delay={(i % 4) * 0.05}>
                <MediaCard item={it} onOpen={(p) => setLb(photos.findIndex((x) => x.id === p.id))} />
              </Reveal>
            </Fragment>
          ))}
        </div>
      )}
      {lb >= 0 && <Lightbox items={photos} index={lb} onIndex={setLb} onClose={() => setLb(-1)} />}
    </div>
  );
}
